// Draft-layer mutations + status for the crew schedule's edit mode.
//
// Edits stage server-side in mp_schedule_drafts (they survive reloads and
// stay invisible to PMs) until the scheduler hits Publish. Reset discards
// them. Every mutation invalidates both the schedule (draft overlay) and the
// unpublished-changes indicator.
//
// Publish and Reset act on YOUR OWN staged cells only — never another
// scheduler's. `othersCount` / `otherEditors` surface their pending work so it
// is visible without being publishable.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { manpowerApi, type DraftStatus } from '@/lib/apiClient';
import { invalidateReadinessQueries, invalidateStartAlignmentQueries } from '@/lib/queryClient';
import type { CrewPhase } from '../lib/scheduleModel';

export interface DraftEntryInput {
  workDate: string;
  projectId: number;
  crewCount: number;
  phase?: CrewPhase;
  isOverride?: boolean;
}

export function useDraftEdits(opts: { enabled: boolean; onError?: (message: string) => void }) {
  const qc = useQueryClient();
  // Never fail silently — a lost edit the scheduler didn't notice is worse
  // than an ugly banner. Surfaces through the page's notification strip.
  const fail = (what: string) => (err: unknown) => {
    const detail = (err as any)?.response?.data?.message ?? (err as any)?.message ?? 'network error';
    opts.onError?.(`${what} failed — ${detail}. Your staged draft is unchanged; try again.`);
    // Re-sync both caches so the UI reflects server truth after the failure.
    qc.invalidateQueries({ queryKey: ['manpower-schedule'] });
    qc.invalidateQueries({ queryKey: ['manpower-draft-status'] });
  };
  const invalidateSchedule = () => qc.invalidateQueries({ queryKey: ['manpower-schedule'] });
  // Draft mutations return the fresh status — write it straight into the
  // cache instead of paying a second round-trip to re-fetch it. On a slow
  // connection that lag left Publish disabled after the last staged edit.
  const setStatus = (s: DraftStatus) =>
    qc.setQueryData(['manpower-draft-status'], s);

  // Unpublished-changes indicator. Polled cheaply for editors even outside
  // edit mode so a forgotten draft is impossible to miss.
  const statusQuery = useQuery({
    queryKey: ['manpower-draft-status'],
    queryFn: manpowerApi.draftStatus,
    enabled: opts.enabled,
    staleTime: 15_000,
  });

  const upsert = useMutation({
    mutationFn: (data: DraftEntryInput) =>
      manpowerApi.upsertDraftEntry({ ...data, phase: data.phase ?? undefined }),
    onSuccess: (status) => { setStatus(status); invalidateSchedule(); },
    onError: fail('Staging the edit'),
  });

  const bulk = useMutation({
    mutationFn: (entries: DraftEntryInput[]) =>
      manpowerApi.bulkDraftEntries(entries.map((e) => ({ ...e, phase: e.phase ?? undefined }))),
    onSuccess: (status) => { setStatus(status); invalidateSchedule(); },
    onError: fail('Staging the edits'),
  });

  // Publish/Reset clear only YOUR drafts, so zero out your own counters
  // optimistically but keep whatever other schedulers have pending — then
  // re-fetch, since their count may have moved while you were editing.
  const clearMine = () => {
    qc.setQueryData<DraftStatus>(['manpower-draft-status'], (prev) => ({
      count: 0,
      lastUpdatedAt: null,
      othersCount: prev?.othersCount ?? 0,
      otherEditors: prev?.otherEditors ?? [],
    }));
    qc.invalidateQueries({ queryKey: ['manpower-draft-status'] });
  };

  const publish = useMutation({
    mutationFn: manpowerApi.publishDrafts,
    // ⚠️ PUBLISH also clears ['today'], and Reset below deliberately does not.
    // Both Today pages read crew off PUBLISHED mp_schedule_entries — the PM
    // page's no-crew action item and the leadership page's 'No crew booked'
    // section — so staging or discarding a draft changes nothing they can see,
    // and publishing changes it for every job in the batch.
    onSuccess: () => {
      clearMine();
      invalidateSchedule();
      void invalidateReadinessQueries(qc);
      // A publish can create or move a job's first booked crew day, which is what
      // the start-mismatch flag is judged against (#385).
      void invalidateStartAlignmentQueries(qc);
    },
    onError: fail('Publish'),
  });

  const reset = useMutation({
    mutationFn: manpowerApi.resetDrafts,
    onSuccess: () => { clearMine(); invalidateSchedule(); },
    onError: fail('Reset'),
  });

  return {
    draftCount: statusQuery.data?.count ?? 0,
    lastDraftAt: statusQuery.data?.lastUpdatedAt ?? null,
    // Other schedulers' pending work — surfaced, but not yours to publish.
    othersDraftCount: statusQuery.data?.othersCount ?? 0,
    otherDraftEditors: statusQuery.data?.otherEditors ?? [],
    upsert,
    bulk,
    publish,
    reset,
    busy: upsert.isPending || bulk.isPending || publish.isPending || reset.isPending,
  };
}
